import React, {
  FC,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Icon from "@Components/ui/icon";
import CmsTextEditorContent from "@Libs/contentful/components/cms-text-editor/CmsTextEditor";
import Image from "components/Image";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import classes from "./UniqueSalesPropositions.module.scss";

export const RIGHT_ARROW_TEST_ID = "slider_right_arrow_test_id";
export const LEFT_ARROW_TEST_ID = "slider_left_arrow_test_id";
export const SLIDER_ITEM_ID = "slider_item_id";

type LazyType = "ondemand" | "progressive";

interface IRefProps extends Slider {
  slickGoTo: (currentSlide: number) => {};
}

interface IArrowProps {
  className?: string;
  currentSlide?: number;
  "data-role"?: string;
  slideCount?: number;
  style?: {};
  onClick?: MouseEventHandler;
}
interface IUniqueSalesProps {
  name: string;
  cta: {
    label: string;
    link: string;
  };
  alignment: string;
  salesPropositions: {
    items: {
      title: string;
      imageCtaLink: string;
      image: {
        url: string;
        title?: string;
        width?: number;
        height?: number;
      };
      content: {
        json: {};
      };
    }[];
  };
}

const UniqueSalesPropositions: FC<IUniqueSalesProps> = (props) => {
  const sliderRef = useRef<IRefProps>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { width } = useWindowDimensions();
  const slidesToShow = width > 991 ? 4 : width > 767 ? 3 : 2;

  useEffect(() => {
    setCurrentSlide(0);
  }, [slidesToShow]);

  useEffect(() => {
    if (sliderRef.current) sliderRef.current.slickGoTo(currentSlide);
  }, [currentSlide]);

  const { name, cta, alignment } = props;
  const { items } = props.salesPropositions;
  if (!items?.length) {
    return null;
  }
  const sectionAlignmentClass = classes[`unique-sales-${alignment}`];
  const containerAlignmentClass =
    classes[`unique-sales-container-${alignment}`];
  const nameAlignmentClass = classes[`unique-sales-name-${alignment}`];
  const imageAlignmentClass = classes[`unique-sales-image-${alignment}`];
  const textAlignmentClass = classes[`unique-sales-text-${alignment}`];
  const ctaAlignmentClass = classes[`unique-sales-cta-${alignment}`];

  const SamplePrevArrow = () => {
    const disabledArrow = currentSlide !== undefined && currentSlide < 1;
    const arrowClass = disabledArrow
      ? `${classes["arrow"]} ${classes["arrow-left"]} ${classes["disabled-arrow"]}`
      : `${classes["arrow"] + " " + classes["arrow-left"]}`;

    const handleLeftArrowClick = () => {
      if (currentSlide && currentSlide > 0)
        setCurrentSlide(currentSlide - slidesToShow);
    };
    return (
      <button
        className={arrowClass}
        onClick={handleLeftArrowClick}
        data-testid={LEFT_ARROW_TEST_ID}
      >
        <Icon iconName="arrow-left-thin" />
      </button>
    );
  };

  const SampleNextArrow = (props: IArrowProps) => {
    const { slideCount } = props;

    const handleRightArrowClick = () => {
      if (slideCount && currentSlide !== undefined && currentSlide < slideCount)
        setCurrentSlide(currentSlide + slidesToShow);
    };
    const disabledArrow =
      currentSlide !== undefined &&
      slideCount !== undefined &&
      currentSlide >= slideCount - slidesToShow;
    const arrowClassBase = `${classes["arrow"]} ${classes["arrow-right"]}`;
    const arrowClass = disabledArrow
      ? `${arrowClassBase} ${classes["disabled-arrow"]}`
      : `${arrowClassBase}`;

    return (
      <button
        className={arrowClass}
        onClick={handleRightArrowClick}
        data-testid={RIGHT_ARROW_TEST_ID}
      >
        <Icon iconName="arrow-right-thin" />
      </button>
    );
  };

  const settings = {
    infinite: false,
    lazyLoad: "progressive" as LazyType,
    swipeDirection: "down",
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToShow,
    arrows: true,
    adaptiveHeight: false,
    prevArrow: <SamplePrevArrow />,
    nextArrow: <SampleNextArrow />,
    dots: false,
    className: classes["section-slider"],
    initialSlide: currentSlide,
    centerMode: alignment === "center" && width > 991 ? true : false,
  };

  return (
    <section className={`section ${classes["unit-sales-section"]}`}>
      {name && (
        <h2 className={`${classes["unique-sales-name"]} ${nameAlignmentClass}`}>
          {name}
        </h2>
      )}
      <div
        className={`section ${classes["unique-sales"]} ${sectionAlignmentClass}`}
      >
        <Slider {...settings} ref={sliderRef}>
          {items.map((item, i) => (
            <div
              key={`sales-proposition-${i}`}
              className={`${classes["unique-sales-container"]} ${containerAlignmentClass}`}
            >
              <div
                className={`${classes["unique-sales-image"]} ${imageAlignmentClass}`}
              >
                <a href={item.imageCtaLink}>
                  <Image
                    src={
                      item.image?.url &&
                      `${item.image.url}?fm=webp&w=1920&h=1080`
                    }
                    alt={item.image?.title ?? ""}
                    objectFit="contain"
                    layout="fill"
                  />
                </a>
              </div>
              <div
                className={`${classes["unique-sales-text"]} ${textAlignmentClass}`}
              >
                <h4 className={classes["unique-sales-title"]}>
                  {item.title ?? ""}
                </h4>
                <span className={classes["unique-sales-desc"]}>
                  <CmsTextEditorContent content={item.content} />
                </span>
              </div>
            </div>
          ))}
        </Slider>
      </div>
      {cta?.label && cta?.link && (
        <a
          className={`${classes["unique-sales-cta"]} ${ctaAlignmentClass}`}
          href={cta.link}
        >
          {cta.label}
        </a>
      )}
    </section>
  );
};

export default UniqueSalesPropositions;
